// @givmo/org-console — an act only a passkey can answer, for an account that holds none, on a host
// where one can be made (MCP-P6d: W5's code-session routing made host-aware; Account Security's
// steward read of cut 45, R1; Compliance's `cannot-meet-check.tsx` is the same pattern).
//
// A session held to the emailed code's limits is refused with its cure named, and the truth of what
// the page can do about it is the HOST's, while the rows are this package's. So the host says: its
// step-up adapter hands `renderPasskeySetupOffer` only where a passkey ceremony can run on this page's
// host (a `givmo.io` host, `@givmo/passkeys`' `passkeysUsableHere`).
//
//   - WHERE IT CAN, a cure is never a dead end. `passkey` reaches the sheet's passkey arm, and an
//     account that holds none (`add_passkey`, or a passkey gone since the refusal) is failed by the gate
//     with `StepUpPasskeyFailure('none_registered')`, which this turns into the host's offer, drawn in
//     the act's place. Nothing runs after a passkey is set up: the act waits for the person's own press,
//     which the passkey then answers. "Not now" leaves the page's line standing, and the act's button
//     gives way to one that reopens the offer here, since pressing the act could only meet the same
//     refusal; it never runs the act. An offer that can't work (no WebAuthn in this browser, a dark
//     surface, a sign-in lock) settles `unavailable`, and the act is shut.
//   - WHERE IT CAN'T (dev's raw Firebase host, whose RP ID isn't `givmo.io`), the page keeps its rows'
//     own lines for those dead ends, because they are true there.

import { useCallback, useState, type ReactNode } from 'react';
import { StepUpPasskeyFailure, useOptionalStepUpHost } from '@givmo/step-up';

export interface FirstPasskey {
  /** A passkey can be used and set up on this page's host: its offer exists. */
  readonly worksHere: boolean;
  /** The act met this case: the page says so, until the next press or a passkey set up. */
  readonly shown: boolean;
  /** The host's offer, drawn in the act's place while it is open; `undefined` otherwise. */
  readonly offer: ReactNode | undefined;
  /** The offer can't work here: a press could only meet the same refusal again. */
  readonly shut: boolean;
  /**
   * Nothing can be set up, known already (a sign-in lock's offer, up and waiting for its "OK") or
   * settled (`shut`): the page must not say to set one up.
   */
  readonly cantBeMade: boolean;
  /** "Not now" was pressed: the line stands, and the page offers `reopen` in the act's place. */
  readonly waiting: boolean;
  /** An act's failure, from its `onError`: `true` when it is this case, which opens the offer. */
  readonly caught: (error: unknown) => boolean;
  /** A new press: the line and an open offer go, until the next refusal. */
  readonly reset: () => void;
  /** Open the offer again, here, asking the server nothing. Never runs the act. */
  readonly reopen: () => void;
}

export function useFirstPasskey(): FirstPasskey {
  const render = useOptionalStepUpHost()?.renderPasskeySetupOffer;
  const worksHere = render !== undefined;
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState(false);
  const [shut, setShut] = useState(false);
  const [locked, setLocked] = useState(false);
  const caught = useCallback(
    (error: unknown) => {
      if (!worksHere) return false;
      if (!(error instanceof StepUpPasskeyFailure) || error.reason !== 'none_registered') {
        return false;
      }
      setShown(true);
      setOpen(true);
      return true;
    },
    [worksHere],
  );
  const reset = useCallback(() => {
    setShown(false);
    setOpen(false);
    setLocked(false);
  }, []);
  const reopen = useCallback(() => setOpen(true), []);
  const onLocked = useCallback(() => setLocked(true), []);
  const offer =
    open && render !== undefined
      ? render({
          variant: 'from-confirm',
          onLocked,
          // "Not now" leaves the line standing; a passkey set up brings the act back without it. An
          // offer that can't work here shuts the act, the line standing.
          onSettled: (outcome) => {
            setOpen(false);
            if (outcome === 'created') setShown(false);
            else if (outcome === 'unavailable') setShut(true);
          },
        })
      : undefined;
  return {
    worksHere,
    shown,
    offer,
    shut: shown && shut,
    cantBeMade: shown && (shut || locked),
    waiting: shown && !open && !shut,
    caught,
    reset,
    reopen,
  };
}
